import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type Ref,
} from 'react';
import { createPortal } from 'react-dom';
import { Box, Flex } from '@chakra-ui/react';
import { useGetResumesQuery } from '@/store/query/ats-contacts';
import { CandidateProfileCard } from '../candidateProfiles/CandidateProfileCard';
import {
  fromAtsResumes,
  type AtsResumeFile,
} from '../candidateProfiles/toResumeAttachments';
import type { CandidateSummary } from '../../../types';

// Bind these to the candidate name so hovering the name (not the whole card)
// opens the panel; the ref also anchors the panel to the thread.
export interface HoverBind {
  ref: Ref<HTMLDivElement>;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

const OPEN_DELAY_MS = 250;
// Grace period so moving the cursor from the card onto the panel doesn't close it.
const CLOSE_DELAY_MS = 150;
const PANEL_WIDTH = 720;
const MARGIN = 16;
// Nudge the centered panel right so the hovered card's avatar + name stay visible.
const NUDGE_RIGHT = 150;
const PANEL_Z = 2100;

// Rect of the area the panel is centered within (the thread scroll area).
interface Bounds {
  top: number;
  left: number;
  width: number;
  height: number;
}

// The scrollable thread area (ThreadBody) sits below the widget header, so its
// rect is the right anchor — the widget's real bounds, not the viewport. Falls
// back to the widget root, then the document.
const findThreadBounds = (el: HTMLElement): DOMRect => {
  let cur: HTMLElement | null = el.parentElement;
  while (cur) {
    const oy = getComputedStyle(cur).overflowY;
    if (oy === 'auto' || oy === 'scroll') return cur.getBoundingClientRect();
    cur = cur.parentElement;
  }
  const container = el.closest(
    '[data-test-id="thread-container"]'
  ) as HTMLElement | null;
  return (container ?? document.documentElement).getBoundingClientRect();
};

// A hover overlay that previews the full candidate profile. The panel is
// centered — both horizontally and vertically — within the thread, like a
// modal, independent of which card is hovered. Opens on hovering the candidate
// name (via the render-prop bind), not the whole card. Reuses the carousel's
// CandidateProfileCard (which self-fetches the contact by id) and feeds it the
// resume from the healthy host ATS endpoint. Everything only loads while the
// panel is open.
export const CandidateHoverProfile = ({
  candidate,
  children,
}: {
  candidate: CandidateSummary;
  children: (bind: HoverBind) => ReactNode;
}) => {
  const triggerRef = useRef<HTMLDivElement>(null);
  const openTimer = useRef<ReturnType<typeof setTimeout>>();
  const closeTimer = useRef<ReturnType<typeof setTimeout>>();
  const [bounds, setBounds] = useState<Bounds | null>(null);
  const open = bounds !== null;

  // Resumes come from the healthy host ATS endpoint (/contacts/:id/resumes),
  // not the flaky agentforge /profile call the card would otherwise use — so
  // the Resume section renders below Skills. Only fetched while the panel is
  // open. Passing it in also disables the card's own (flaky) resume fetch.
  const { data: atsResumes, isError: resumesError } = useGetResumesQuery(
    { contactId: candidate.id },
    { skip: !open }
  ) as { data?: AtsResumeFile[]; isError: boolean };
  const resumes = fromAtsResumes(atsResumes ?? []);
  // Loading = panel open but the resume hasn't resolved (or errored) yet. More
  // reliable than RTK's isLoading across the skip→active gap, and it keeps the
  // card's spinner up until we know resume-vs-details (no content flash).
  const resumesLoading = open && !resumesError && atsResumes === undefined;

  const clearOpen = () => openTimer.current && clearTimeout(openTimer.current);
  const clearClose = () =>
    closeTimer.current && clearTimeout(closeTimer.current);

  useEffect(
    () => () => {
      clearOpen();
      clearClose();
    },
    []
  );

  const openNow = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = findThreadBounds(el);
    setBounds({ top: r.top, left: r.left, width: r.width, height: r.height });
  }, []);

  const scheduleOpen = () => {
    clearClose();
    clearOpen();
    openTimer.current = setTimeout(openNow, OPEN_DELAY_MS);
  };
  const scheduleClose = () => {
    clearOpen();
    clearClose();
    closeTimer.current = setTimeout(() => setBounds(null), CLOSE_DELAY_MS);
  };

  return (
    <>
      {children({
        ref: triggerRef,
        onMouseEnter: scheduleOpen,
        onMouseLeave: scheduleClose,
      })}
      {bounds &&
        createPortal(
          // Centering container over the thread; transparent to pointer events
          // so hovering cards behind it still works. Only the panel captures.
          <Flex
            position="fixed"
            top={`${bounds.top}px`}
            left={`${bounds.left}px`}
            width={`${bounds.width}px`}
            height={`${bounds.height}px`}
            align="center"
            justify="center"
            p={`${MARGIN}px`}
            pointerEvents="none"
            zIndex={PANEL_Z}
          >
            <Box
              width={`${Math.min(PANEL_WIDTH, bounds.width - MARGIN * 2)}px`}
              maxHeight="100%"
              transform={`translateX(${NUDGE_RIGHT}px)`}
              pointerEvents="auto"
              onMouseEnter={clearClose}
              onMouseLeave={scheduleClose}
              data-test-id={`agentforge-candidate-hover-panel-${candidate.id}`}
            >
              <CandidateProfileCard
                candidate={candidate}
                active
                resumes={resumes}
                resumesLoading={resumesLoading}
              />
            </Box>
          </Flex>,
          document.body
        )}
    </>
  );
};
